import { Box, Button, IconButton, MenuItem, Select, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { RemainderMode } from "../../types/dataReview";
import { FAINT, LINE, MONO, MUTED, TEXT, btnPrimary, calloutSx, capSx, cardSx, floatSx, inputSx, menuItemSx, radioSx, swatchSx } from "./editorTheme";
import { fmtArea } from "./editorModel";
import { useEditorData } from "./useEditorData";
import { useEditorActions } from "./useEditorActions";
import { useVegEditor } from "./VegEditorContext";

const REMAINDER_OPTS: { v: RemainderMode; title: string; sub: string }[] = [
  { v: "none", title: "Leave it unmapped", sub: "No vegetation there: nothing burns" },
  { v: "orig", title: "What it was", sub: "Keep the current layer under the gaps" },
  { v: "class", title: "One vegetation class", sub: "Fill all of it with a class you pick" },
];

/** The review sheet on the right: before / after, what the rest of a
 *  clean slate becomes, the net change by class, and Apply. */
export default function VegEditorSheet() {
  const d = useEditorData();
  const a = useEditorActions();
  const { setSheet, before, setBefore, remainder, setRemainder } = useVegEditor();
  const s = d.jobSummary;
  const mode = remainder?.mode ?? d.job?.options?.remainder?.mode ?? null;
  const cls = remainder?.veg_class ?? d.job?.options?.remainder?.veg_class ?? "";
  const classChoices = Array.from(new Set(["Lawn", ...d.classes, "Shrub", "Tree"]));
  const pick = (v: RemainderMode) => setRemainder({ mode: v, veg_class: v === "class" ? (cls || classChoices[0]) : "" });
  const seg = (on: boolean) => ({
    flex: 1, height: 28, borderRadius: 1.5, fontSize: 12.5, fontWeight: 500, textTransform: "none" as const, minWidth: 0,
    color: on ? TEXT : MUTED, bgcolor: on ? "rgba(255,255,255,0.12)" : "transparent", "&:hover": { bgcolor: on ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.06)" },
  });
  const empty = !!s && !s.rows.length && !s.assets;
  const applyOff = !s || s.needs_remainder || (empty && !d.isClean) || a.busy.apply || d.applying;
  return (
    <Box sx={{ ...floatSx, position: "fixed", top: "calc(var(--topbar-h) + var(--strategies-h) + 12px)", right: 12, bottom: 12, width: 360, zIndex: 1110, display: "flex", flexDirection: "column" }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Review changes</Typography>
          <IconButton size="small" onClick={() => { setSheet(null); setBefore(false); }} aria-label="Close" sx={{ color: MUTED }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
        </Box>
        <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.5, lineHeight: 1.5 }}>The fire model keeps using the current layer until you apply.</Typography>
        <Box sx={{ display: "flex", gap: 0.25, p: 0.25, mt: 1.5, borderRadius: 2, bgcolor: "rgba(0,0,0,0.22)", border: `1px solid ${LINE}` }}>
          <Button sx={seg(before)} onClick={() => setBefore(true)}>Before</Button>
          <Button sx={seg(!before)} onClick={() => setBefore(false)}>After</Button>
        </Box>
        {!!s && d.isClean && s.unmapped_m2 > 0.5 && (
          <Box sx={{ ...calloutSx, mt: 2, p: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{fmtArea(s.unmapped_m2)} of the area isn't mapped yet</Typography>
            </Box>
            <Typography sx={{ fontSize: 12, mt: 0.75, mb: 1.25, lineHeight: 1.5 }}>Choose what it becomes before you apply.</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {REMAINDER_OPTS.map((o) => (
                <Box key={o.v} sx={{ ...cardSx(mode === o.v), p: "8px 10px" }} onClick={() => pick(o.v)}>
                  <Box sx={radioSx(mode === o.v)} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: TEXT }}>{o.title}</Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED }}>{o.sub}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            {mode === "class" && (
              <Select size="small" value={cls || classChoices[0]} onChange={(ev) => setRemainder({ mode: "class", veg_class: String(ev.target.value) })} sx={{ ...inputSx, mt: 1, height: 30, width: "100%" }} MenuProps={{ PaperProps: { sx: { bgcolor: "#1d2733", color: TEXT } } }} aria-label="Class for the rest of the area">
                {classChoices.map((c) => <MenuItem key={c} value={c} sx={menuItemSx}>{c}</MenuItem>)}
              </Select>
            )}
            <Button onClick={() => { setSheet(null); setBefore(false); }} sx={{ textTransform: "none", fontSize: 11.5, color: "#f3dcae", p: 0, mt: 1.25, minWidth: 0, "&:hover": { bgcolor: "transparent", color: TEXT } }}>Keep mapping instead</Button>
          </Box>
        )}
        <Typography sx={{ ...capSx, mt: 2.25, mb: 0.5 }}>{d.isClean ? "The area becomes" : "This refinement writes"}</Typography>
        {s?.rows.map((r) => (
          <Box key={r.veg_class} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.75 }}>
            <Box sx={swatchSx(d.colours[r.veg_class] ?? "#b35bd1")} />
            <Typography sx={{ fontSize: 13, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.veg_class}>{r.veg_class}</Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 12, color: "#9ff0d4" }}>{fmtArea(r.area_m2)}</Typography>
          </Box>
        ))}
        {!!s && d.isClean && s.buildings_m2 > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.75 }}>
            <Box sx={swatchSx(d.colours.Building ?? "#c9a66b")} />
            <Typography sx={{ fontSize: 13, flex: 1 }}>Buildings, kept</Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 12, color: MUTED }}>{fmtArea(s.buildings_m2)}</Typography>
          </Box>
        )}
        {!!s && d.isClean && s.unmapped_m2 > 0.5 && mode !== "class" && mode !== "orig" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.75 }}>
            <Box sx={{ ...swatchSx("transparent"), border: `1px solid ${FAINT}`, backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.8) 0 1.5px, transparent 1.5px 4px)" }} />
            <Typography sx={{ fontSize: 13, flex: 1 }}>Not mapped yet</Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 12, color: "#f5cd84" }}>{fmtArea(s.unmapped_m2)}</Typography>
          </Box>
        )}
        {!!s && !s.rows.length && !(d.isClean && s.unmapped_m2 > 0.5) && <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.5 }}>No vegetation changes yet.</Typography>}
        {!!s && (
          <Box sx={{ borderTop: `1px solid ${LINE}`, mt: 1.5, pt: 1.25 }}>
            {([["Shapes accepted", s.shapes.toLocaleString()], ["Painted by hand", s.painted.toLocaleString()], ["New assets", s.assets.toLocaleString()], ["Area changed", fmtArea(s.changed_m2)]] as const).map(([k, v]) => (
              <Box key={k} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}><Typography sx={{ fontSize: 12.5, color: MUTED }}>{k}</Typography><Typography sx={{ fontFamily: MONO, fontSize: 12 }}>{v}</Typography></Box>
            ))}
          </Box>
        )}
        <Typography sx={{ fontSize: 12, color: MUTED, mt: 1.75, lineHeight: 1.5 }}>{d.isClean ? "Everything else inside the area is cleared when you apply. " : "Accepted shapes replace what is under them when you apply. "}Fuel loads recalculate afterwards, and simulations that use this vegetation are flagged for re-run. You can undo straight after applying.</Typography>
      </Box>
      <Box sx={{ p: "12px 16px", borderTop: `1px solid ${LINE}` }}>
        <Button sx={{ ...btnPrimary, width: "100%", height: 36 }} disabled={applyOff} onClick={a.apply}>
          {d.applying || a.busy.apply ? "Applying…" : s?.needs_remainder ? "Choose what the rest becomes" : "Apply to the vegetation layer"}
        </Button>
      </Box>
    </Box>
  );
}
